import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ShieldCheck, Users, Eye, Check, X, ArrowsClockwise, LockSimple, Bank } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_da191abd-9276-45a7-bd48-1757c1f4e471/artifacts/p5urxzpf_diametor_logo_black__1_-removebg-preview.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_debt-cycle/artifacts/gzyrfqiy_diametor_logo_white-removebg-preview.png";

function VerImage({ path, label, isDark }) {
  const [imgUrl, setImgUrl] = useState(null);
  useEffect(() => {
    if (!path) return;
    const fetchImg = async () => {
      try {
        const res = await axios.get(`${API}/files/${path}?admin=support`, { responseType: 'blob' });
        setImgUrl(URL.createObjectURL(res.data));
      } catch (e) { /* ignore */ }
    };
    fetchImg();
    return () => { if (imgUrl) URL.revokeObjectURL(imgUrl); };
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <p className={`font-ibm text-[10px] uppercase tracking-wider mb-1 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{label}</p>
      <div className={`w-40 h-28 border rounded-sm overflow-hidden ${isDark ? 'border-zinc-700 bg-zinc-800' : 'border-zinc-300 bg-zinc-100'}`}>
        {imgUrl ? (
          <img src={imgUrl} alt={label} className="w-full h-full object-cover" />
        ) : (
          <p className={`text-xs text-center pt-10 ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>Loading...</p>
        )}
      </div>
    </div>
  );
}

export default function SupportPanel() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [isAdmin, setIsAdmin] = useState(null);
  const [verifications, setVerifications] = useState([]);
  const [loans, setLoans] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check admin access first
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await axios.get(`${API}/support/check-admin`, { withCredentials: true });
        setIsAdmin(res.data.is_admin);
      } catch {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [verRes, usersRes, loansRes] = await Promise.all([
        axios.get(`${API}/support/verifications`, { withCredentials: true }),
        axios.get(`${API}/support/users`, { withCredentials: true }),
        axios.get(`${API}/support/loans`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setVerifications(verRes.data);
      setUsers(usersRes.data);
      setLoans(loansRes.data || []);
    } catch (err) {
      console.error('Failed to fetch support data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) fetchData(); }, [isAdmin, fetchData]);

  const fetchUserDetails = async (userId) => {
    try {
      const res = await axios.get(`${API}/support/users/${userId}/details`, { withCredentials: true });
      setUserDetails(res.data);
      setSelectedUser(userId);
    } catch (err) {
      toast.error('Failed to fetch user details');
    }
  };

  const handleVerification = async (verId, action) => {
    try {
      await axios.post(`${API}/support/verifications/${verId}/respond`, { action }, { withCredentials: true });
      toast.success(`Verification ${action}d`);
      fetchData();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleLoan = async (loanId, action) => {
    try {
      await axios.post(`${API}/support/loans/${loanId}/respond`, { action }, { withCredentials: true });
      toast.success(`Loan ${action}d`);
      fetchData();
    } catch (err) {
      toast.error('Failed');
    }
  };

  // Admin check loading
  if (isAdmin === null) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-zinc-950' : 'bg-white'}`}>
        <div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Access denied
  if (!isAdmin) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-zinc-950' : 'bg-white'}`} data-testid="support-access-denied">
        <div className="text-center max-w-sm px-6">
          <LockSimple size={48} weight="bold" className={`mx-auto mb-4 ${isDark ? 'text-zinc-600' : 'text-zinc-300'}`} />
          <h2 className={`font-outfit text-xl font-bold mb-2 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Access Denied</h2>
          <p className={`font-ibm text-sm mb-6 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
            You don't have permission to access the Support Panel. This area is restricted to administrators.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="h-10 px-6 bg-yellow-400 text-zinc-950 font-ibm text-sm font-semibold rounded-sm hover:bg-yellow-500 transition-all"
            data-testid="back-to-dashboard"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-zinc-950' : 'bg-white'}`}>
        <div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-white'}`} data-testid="support-panel">
      <div className={`py-4 px-6 border-b flex items-center gap-3 ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
        <img src={isDark ? LOGO_DARK : LOGO_LIGHT} alt="Diametor" className="h-7" />
        <span className={`font-ibm text-xs font-semibold uppercase tracking-[0.2em] px-2 py-1 rounded-sm ${isDark ? 'bg-yellow-400/10 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>Support Panel</span>
      </div>

      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <Tabs defaultValue="verifications">
          <TabsList className={`mb-6 ${isDark ? 'bg-zinc-900' : 'bg-zinc-100'}`}>
            <TabsTrigger value="verifications" className="font-ibm text-sm gap-1">
              <ShieldCheck size={16} /> Verifications ({verifications.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="font-ibm text-sm gap-1">
              <Users size={16} /> All Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="loans" className="font-ibm text-sm gap-1" data-testid="tab-loans">
              <Bank size={16} /> Loans ({loans.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="verifications">
            {verifications.length === 0 ? (
              <p className={`font-ibm text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>No pending verifications</p>
            ) : (
              <div className="space-y-4">
                {verifications.map(v => (
                  <div key={v.verification_id} className={`p-4 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid={`verification-${v.verification_id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{v.name || v.user?.name}</p>
                        <p className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{v.email || v.user?.email}</p>
                        <p className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{v.country_code} {v.phone}</p>
                        <p className={`font-ibm text-[10px] mt-1 ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>Submitted: {new Date(v.created_at).toLocaleString()}</p>
                        {v.is_edit && <span className="font-ibm text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-500/10 text-blue-400">Bank details re-verification</span>}
                        <div className="flex gap-4 mt-3">
                          {v.govt_id_path && <VerImage path={v.govt_id_path} label="Govt ID" isDark={isDark} />}
                          {v.selfie_path && <VerImage path={v.selfie_path} label="Selfie w/ ID" isDark={isDark} />}
                        </div>
                        <div className={`mt-3 p-3 rounded-sm border ${isDark ? 'border-zinc-700 bg-zinc-900/50' : 'border-zinc-200 bg-white'}`} data-testid={`bank-details-${v.verification_id}`}>
                          <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Bank Details (for payout)</p>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            <div><span className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Account Holder</span><p className={`font-ibm text-xs ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{v.full_name || '—'}</p></div>
                            <div><span className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Account Number</span><p className={`font-ibm text-xs font-mono ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{v.account_number || '—'}</p></div>
                            <div><span className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>IFSC</span><p className={`font-ibm text-xs font-mono ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{v.ifsc || '—'}</p></div>
                            <div><span className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Bank</span><p className={`font-ibm text-xs ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{v.bank_name || '—'}</p></div>
                            <div><span className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Branch</span><p className={`font-ibm text-xs ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{v.branch || '—'}</p></div>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => handleVerification(v.verification_id, 'approve')} className="h-8 px-4 bg-green-500/10 border border-green-500/30 text-green-500 font-ibm text-xs font-semibold rounded-sm hover:bg-green-500/20 flex items-center gap-1" data-testid={`approve-ver-${v.verification_id}`}>
                          <Check size={14} weight="bold" /> Approve
                        </button>
                        <button onClick={() => handleVerification(v.verification_id, 'reject')} className="h-8 px-4 bg-red-500/10 border border-red-500/30 text-red-400 font-ibm text-xs font-semibold rounded-sm hover:bg-red-500/20 flex items-center gap-1" data-testid={`reject-ver-${v.verification_id}`}>
                          <X size={14} weight="bold" /> Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="users">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* User List */}
              <div className={`lg:col-span-1 border rounded-md overflow-hidden ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
                <div className={`p-3 ${isDark ? 'bg-zinc-900' : 'bg-zinc-50'}`}>
                  <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Users ({users.length})</p>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  {users.map(u => (
                    <button key={u.user_id} onClick={() => fetchUserDetails(u.user_id)} className={`w-full p-3 text-left border-b transition-colors ${selectedUser === u.user_id ? (isDark ? 'bg-yellow-400/10 border-yellow-400/20' : 'bg-yellow-50 border-yellow-200') : (isDark ? 'border-zinc-800 hover:bg-zinc-900' : 'border-zinc-200 hover:bg-zinc-50')}`} data-testid={`user-row-${u.user_id}`}>
                      <p className={`font-ibm text-sm font-medium truncate ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{u.name}</p>
                      <p className={`font-ibm text-xs truncate ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{u.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {u.documents_verified ? <span className="font-ibm text-[10px] text-green-500">Verified</span> : <span className="font-ibm text-[10px] text-yellow-400">Unverified</span>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* User Details */}
              <div className="lg:col-span-2">
                {userDetails ? (
                  <div className={`border rounded-md ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`} data-testid="user-details-panel">
                    <div className={`p-4 ${isDark ? 'bg-zinc-900' : 'bg-zinc-50'}`}>
                      <h3 className={`font-outfit text-lg font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{userDetails.user.name}</h3>
                      <p className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{userDetails.user.email} | {userDetails.user.country_code} {userDetails.user.phone}</p>
                      <div className="flex gap-4 mt-2">
                        <div><span className={`font-ibm text-[10px] uppercase ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Currency</span><p className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{userDetails.user.currency}</p></div>
                        <div><span className={`font-ibm text-[10px] uppercase ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Units</span><p className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{userDetails.units_balance}</p></div>
                        <div><span className={`font-ibm text-[10px] uppercase ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Verified</span><p className={`font-ibm text-sm font-semibold ${userDetails.user.documents_verified ? 'text-green-500' : 'text-yellow-400'}`}>{userDetails.user.documents_verified ? 'Yes' : 'No'}</p></div>
                      </div>
                    </div>

                    {/* Tors */}
                    <div className={`p-4 border-t ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
                      <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Debtors & Creditors ({userDetails.tors.length})</p>
                      {userDetails.tors.length === 0 ? <p className={`font-ibm text-xs ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>None</p> : (
                        <div className="space-y-1">
                          {userDetails.tors.map(t => (
                            <div key={t.tor_id} className={`flex items-center justify-between p-2 rounded-sm ${isDark ? 'bg-zinc-900/50' : 'bg-zinc-50'}`}>
                              <div>
                                <span className={`font-ibm text-xs font-medium ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{t.name}</span>
                                <span className={`font-ibm text-[10px] ml-2 px-1.5 py-0.5 rounded-sm ${t.tor_type === 'debtor' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-400'}`}>{t.tor_type}</span>
                                <p className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{t.email} {t.phone}</p>
                              </div>
                              <span className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{t.currency} {t.amount?.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Clearance Node */}
                    <div className={`p-4 border-t ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
                      <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Clearance Node</p>
                      {!userDetails.node ? <p className={`font-ibm text-xs ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>No active clearance node</p> : (
                        <div className={`flex items-center justify-between p-2 rounded-sm ${isDark ? 'bg-zinc-900/50' : 'bg-zinc-50'}`}>
                          <div className="flex items-center gap-2">
                            <ArrowsClockwise size={14} className="text-yellow-400" />
                            <span className={`font-ibm text-xs ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Fee {'\u20B9'}{(userDetails.node.amount_payable || 0).toLocaleString()} ({userDetails.node.fee_method})</span>
                          </div>
                          <span className={`font-ibm text-xs ${userDetails.node.fee_status === 'paid' || userDetails.node.fee_status === 'charged' ? 'text-green-500' : userDetails.node.fee_status === 'mandate_active' ? 'text-blue-400' : 'text-yellow-400'}`}>{userDetails.node.fee_status}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={`border rounded-md p-8 text-center ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
                    <Eye size={32} className={isDark ? 'text-zinc-700 mx-auto mb-2' : 'text-zinc-300 mx-auto mb-2'} />
                    <p className={`font-ibm text-sm ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>Select a user to view details</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="loans">
            {loans.length === 0 ? (
              <p className={`font-ibm text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>No loans pending verification</p>
            ) : (
              <div className="space-y-4">
                {loans.map(l => (
                  <div key={l.loan_id} className={`p-4 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid={`loan-review-${l.loan_id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Bank size={16} className="text-yellow-400" />
                          <p className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{l.institution}</p>
                          <span className="font-outfit text-sm font-bold text-yellow-400">{'\u20B9'}{(l.loan_amount || 0).toLocaleString()}</span>
                        </div>
                        <p className={`font-ibm text-xs mt-1 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Borrower: {l.user_name} · {l.user_email} · {l.user_phone}</p>
                        <div className={`mt-3 p-3 rounded-sm border grid grid-cols-2 gap-x-4 gap-y-1 ${isDark ? 'border-zinc-700 bg-zinc-900/50' : 'border-zinc-200 bg-white'}`}>
                          <div><span className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Loan Account No.</span><p className={`font-ibm text-xs font-mono ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{l.account_number}</p></div>
                          <div><span className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Branch / City</span><p className={`font-ibm text-xs ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{l.branch_city || '—'}</p></div>
                          <div><span className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Platform Fee (20%)</span><p className={`font-ibm text-xs ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{'\u20B9'}{(l.fee || 0).toLocaleString()}</p></div>
                          <div><span className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Submitted</span><p className={`font-ibm text-xs ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{new Date(l.created_at).toLocaleString()}</p></div>
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => handleLoan(l.loan_id, 'approve')} className="h-8 px-4 bg-green-500/10 border border-green-500/30 text-green-500 font-ibm text-xs font-semibold rounded-sm hover:bg-green-500/20 flex items-center gap-1" data-testid={`approve-loan-${l.loan_id}`}>
                          <Check size={14} weight="bold" /> Verify & Lock
                        </button>
                        <button onClick={() => handleLoan(l.loan_id, 'reject')} className="h-8 px-4 bg-red-500/10 border border-red-500/30 text-red-400 font-ibm text-xs font-semibold rounded-sm hover:bg-red-500/20 flex items-center gap-1" data-testid={`reject-loan-${l.loan_id}`}>
                          <X size={14} weight="bold" /> Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
